/**
 * Cliente HTTP de la API.
 *
 * Reglas:
 *  · El access token vive SOLO en memoria (nunca en localStorage) — anti-XSS (§13).
 *  · El refresh viaja en cookie HttpOnly; ante un 401 se renueva y se reintenta una vez.
 *  · Los errores se normalizan a `ApiProblem` (RFC 9457) para que la UI muestre el copy
 *    correcto desde @thepit/i18n.
 *  · Si no hay red, se lanza `OfflineError` y el outbox se encarga (ADR-0007).
 */
import type { Problem } from '@thepit/contracts';

function getBaseApiUrl(): string {
  const envUrl = import.meta.env.VITE_API_URL;
  if (envUrl && typeof envUrl === 'string' && envUrl.trim().length > 0) {
    return envUrl.trim();
  }
  if (typeof window !== 'undefined') {
    const { hostname } = window.location;
    if (hostname.endsWith('pages.dev') || hostname === 'thepit.pages.dev') {
      return 'https://thepit-api.fly.dev';
    }
  }
  return 'http://localhost:8080';
}

export const API_URL: string = getBaseApiUrl();

export class ApiProblem extends Error {
  readonly problem: Problem;
  constructor(problem: Problem) {
    super(problem.detail);
    this.name = 'ApiProblem';
    this.problem = problem;
  }
  get code(): string {
    return this.problem.code;
  }
  get status(): number {
    return this.problem.status;
  }
}

export class OfflineError extends Error {
  constructor(message = 'Sin conexión') {
    super(message);
    this.name = 'OfflineError';
  }
}

type TokenListener = (token: string | null) => void;

let accessToken: string | null = null;
const tokenListeners = new Set<TokenListener>();

export function setAccessToken(token: string | null): void {
  accessToken = token;
  for (const listener of tokenListeners) listener(token);
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function onTokenChange(listener: TokenListener): () => void {
  tokenListeners.add(listener);
  return () => tokenListeners.delete(listener);
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /** Clave de idempotencia. Si se omite, se genera una nueva. */
  idempotencyKey?: string;
  terminalId?: string | null;
  signal?: AbortSignal;
  /** No intenta refresh automático (para el propio /auth/refresh). */
  skipRefresh?: boolean;
}

let refreshPromise: Promise<boolean> | null = null;

async function refreshSession(): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        let storedToken: string | null = null;
        try {
          storedToken = localStorage.getItem('pit_refresh_token');
        } catch {
          /* ignore */
        }
        const res = await fetch(`${API_URL}/api/v1/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: storedToken ? JSON.stringify({ refreshToken: storedToken }) : undefined,
        });
        if (!res.ok) return false;
        const data = (await res.json()) as { accessToken: string; refreshToken?: string };
        setAccessToken(data.accessToken);
        if (data.refreshToken) {
          try {
            localStorage.setItem('pit_refresh_token', data.refreshToken);
          } catch {
            /* ignore */
          }
        }
        return true;
      } catch {
        return false;
      } finally {
        refreshPromise = null;
      }
    })();
  }
  return refreshPromise;
}

export function newIdempotencyKey(): string {
  const random = crypto.getRandomValues(new Uint8Array(12));
  const hex = Array.from(random, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${Date.now().toString(36)}-${hex}`;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (accessToken) headers.authorization = `Bearer ${accessToken}`;
  if (options.idempotencyKey) headers['idempotency-key'] = options.idempotencyKey;
  if (options.terminalId) headers['x-terminal-id'] = options.terminalId;

  let response: Response;
  try {
    response = await fetch(`${API_URL}${path}`, {
      method,
      headers,
      credentials: 'include',
      ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    if ((error as Error).name === 'AbortError') throw error;
    throw new OfflineError();
  }

  if (response.status === 401 && !options.skipRefresh) {
    const renewed = await refreshSession();
    if (renewed) {
      return apiRequest<T>(path, { ...options, skipRefresh: true });
    }
  }

  if (!response.ok) {
    let problem: Problem;
    try {
      problem = (await response.json()) as Problem;
    } catch {
      problem = {
        type: 'about:blank',
        title: 'Error',
        status: response.status,
        detail: `HTTP ${response.status}`,
        code: 'INTERNAL',
      };
    }
    throw new ApiProblem(problem);
  }

  if (response.status === 204) return undefined as T;
  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => apiRequest<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: 'PUT', body }),
};

export { refreshSession };
